import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import {
  LayoutDashboard, MessageSquare, Building2, Trash2, DollarSign, CreditCard, LogOut,
  Menu, Loader2, HeadphonesIcon, X, Users, Sun, Moon, MailCheck, Hourglass, Webhook
} from 'lucide-react';

const ADMIN_LOGO = 'https://customer-assets.emergentagent.com/job_8c0fe4ee-111e-41b1-aff2-64466f27b787/artifacts/dwo3qstc_Stock%20%26%20Load%20Admin.png';

const NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
  { to: '/admin/tenants', label: 'Tenants', icon: Building2 },
  { to: '/admin/team', label: 'Admin Team', icon: Users },
  { to: '/admin/marketing', label: 'Marketing', icon: MailCheck },
  { to: '/admin/cleanup-queue', label: 'Cleanup Queue', icon: Hourglass },
  { to: '/admin/twilio-webhooks', label: 'Twilio Webhooks', icon: Webhook },
  { to: '/admin/purge-audit', label: 'Purge Audit', icon: Trash2 },
  { to: '/admin/platform-revenue', label: 'Revenue', icon: DollarSign },
  { to: '/admin/support', label: 'Support', icon: HeadphonesIcon },
];

const STRIPE_ITEM = { to: '/admin/stripe-test', label: 'Stripe Test', icon: CreditCard };

function useAdminTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem('admin_theme') === 'dark');
  const toggle = () => {
    setDark(prev => {
      const next = !prev;
      localStorage.setItem('admin_theme', next ? 'dark' : 'light');
      return next;
    });
  };
  return { dark, toggle };
}

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stripeToolsEnabled, setStripeToolsEnabled] = useState(false);
  const [checking, setChecking] = useState(true);
  const [adminRole, setAdminRole] = useState('member');
  const { dark, toggle: toggleTheme } = useAdminTheme();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    (async () => {
      try {
        const [stripeRes, meRes] = await Promise.allSettled([
          adminAPI.getStripeTestStatus(),
          adminAPI.getMe(),
        ]);
        if (stripeRes.status === 'fulfilled') setStripeToolsEnabled(stripeRes.value.data?.enabled ?? false);
        if (meRes.status === 'fulfilled') setAdminRole(meRes.value.data?.role ?? 'member');
      } catch {}
      setChecking(false);
    })();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-white/60" />
      </div>
    );
  }

  const allNavItems = stripeToolsEnabled ? [...NAV_ITEMS, STRIPE_ITEM] : NAV_ITEMS;

  const SidebarNav = ({ onNavigate }) => (
    <nav className="flex-1 px-3 py-2 space-y-1" data-testid="admin-sidebar-nav">
      {allNavItems.map((item) => {
        const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              active
                ? 'bg-white/15 text-white'
                : 'text-white/60 hover:bg-white/8 hover:text-white/90'
            }`}
          >
            <item.icon className="w-[18px] h-[18px] shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  const ThemeToggle = ({ className = '' }) => (
    <button
      onClick={toggleTheme}
      data-testid="admin-theme-toggle"
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full text-white/60 hover:bg-white/8 hover:text-white/90 transition-all duration-150 ${className}`}
    >
      {dark ? <Sun className="w-[18px] h-[18px] shrink-0" /> : <Moon className="w-[18px] h-[18px] shrink-0" />}
      <span>{dark ? 'Light Mode' : 'Dark Mode'}</span>
    </button>
  );

  return (
    <div className={`min-h-screen flex ${dark ? 'dark' : ''}`} data-testid="admin-layout">
      {/* Desktop sidebar — always dark chrome */}
      <aside
        className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:z-40 md:w-56 md:flex-col"
        data-testid="admin-desktop-sidebar"
      >
        <div className="flex flex-col h-full bg-slate-900">
          <div className="flex items-center justify-center px-4 pt-5 pb-3">
            <NavLink to="/admin/dashboard">
              <img
                src={ADMIN_LOGO}
                alt="Stock & Load Admin"
                className="h-16 w-auto object-contain"
                data-testid="admin-sidebar-logo"
              />
            </NavLink>
          </div>

          <div className="mx-4 border-t border-white/10 mb-2" />

          <SidebarNav />

          {/* Bottom: Theme toggle + Logout */}
          <div className="mt-auto px-3 pb-4">
            <div className="mx-1 border-t border-white/10 mb-3" />
            <ThemeToggle />
            <button
              onClick={handleLogout}
              data-testid="admin-nav-logout"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-150"
            >
              <LogOut className="w-[18px] h-[18px] shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area — themed bg */}
      <div className="flex-1 md:ml-56 flex flex-col min-h-screen overflow-x-hidden bg-background">
        {/* Mobile top bar */}
        <header className="md:hidden bg-slate-900 sticky top-0 z-30 px-4 h-14 flex items-center justify-between" data-testid="admin-mobile-header">
          <img
            src={ADMIN_LOGO}
            alt="Stock & Load Admin"
            className="h-9 w-auto object-contain"
          />
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-white hover:bg-white/10" data-testid="admin-theme-toggle-mobile">
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="admin-mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-slate-900 border-r-0">
                <SheetTitle className="sr-only">Admin Navigation</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between px-4 pt-5 pb-3">
                    <img src={ADMIN_LOGO} alt="Stock & Load Admin" className="h-12 w-auto object-contain" />
                    <Button variant="ghost" size="icon" className="text-white/60 hover:bg-white/10" onClick={() => setMobileOpen(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="mx-4 border-t border-white/10 mb-2" />
                  <SidebarNav onNavigate={() => setMobileOpen(false)} />
                  <div className="mt-auto px-3 pb-4">
                    <div className="mx-1 border-t border-white/10 mb-3" />
                    <ThemeToggle />
                    <button
                      onClick={handleLogout}
                      data-testid="admin-nav-logout-mobile"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-150"
                    >
                      <LogOut className="w-[18px] h-[18px] shrink-0" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 w-full">
          <Outlet context={{ stripeToolsEnabled, adminRole }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
